<template>
  <main class="min-h-screen bg-white overflow-hidden">
    
    <!-- Background elements -->
    <UnderwaterBg class="fixed inset-0 z-0" />

    <!-- Demo Mode Indicator -->
    <div class="absolute top-0 right-0 bg-warning px-3 py-1 rounded-bl-lg text-white font-bold text-sm z-50">
      DEMO MODE
    </div>

    <!-- Header with Back Button -->
    <div class="max-w-7xl mx-auto px-4 mb-6 relative z-10">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <ShimmerButton
          shimmer-color="rgba(255, 255, 255, 0.8)"
          background="#eab308"
          class="px-6 py-2 font-medium inline-flex items-center gap-2 text-white"
          @click="goBack"
        >
          <v-icon class="mr-1">mdi-arrow-left</v-icon>
          Back to Demo Admin
        </ShimmerButton>
      </div>
    </div>
    
    <view-toggle v-model="isDuckView" />
    <!-- Header with Logo - Improved mobile padding -->
    <div class="relative z-10 flex justify-center py-4 md:py-8 animate-fade-in px-4">
      <router-link to="/demo" class="group flex items-center gap-3 transition-transform hover:scale-105">
        <div class="relative">
          <img src="/ducksImages/duckIcon.webp" class="w-12 h-12 drop-shadow-xl group-hover:animate-wiggle" alt="DuckNSeek Logo" />
          <div class="absolute -bottom-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center transform group-hover:scale-125 transition-transform">
            <span class="text-xs font-bold text-white">!</span>
          </div>
        </div>
        <div class="flex flex-col">
          <div class="flex items-center">
            <h1 class="font-bold text-xl md:text-2xl text-slate-800">DuckNSeek Demo</h1>
            <span class="ml-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full inline-flex items-center">
              <v-icon size="12" class="mr-0.5">mdi-information</v-icon>Demo Mode
            </span>
          </div>
          <span class="text-xs text-gray-600 group-hover:text-yellow-600 transition-colors">Click to return to demo admin</span>
        </div>
      </router-link>
    </div>

    <!-- Main Content - Better padding on small screens -->
    <div class="relative z-10 px-3 sm:px-6 md:px-10 max-w-4xl mx-auto py-3 sm:py-6 flex-grow">
      
      <div v-if="loading" class="flex flex-col items-center justify-center h-96 text-center">
        <v-progress-circular indeterminate size="64" color="primary" class="mb-4" />
        <p class="text-gray-700 animate-pulse mt-4">Loading duck information...</p>
      </div>
      
      <template v-else-if="!currentDuck">
        <div class="text-center p-10 max-w-4xl mx-auto bg-white/80 rounded-lg shadow-md backdrop-blur-sm">
          <img src="/ducksImages/duckIcon.webp" class="w-24 h-24 mx-auto mb-4 opacity-70" />
          <h2 class="text-2xl font-bold text-gray-800 mb-3">Duck Not Found!</h2>
          <p class="text-gray-600 mb-6">
            We couldn't find the duck you're looking for. It might have gone swimming elsewhere.
          </p>
          <v-btn color="primary" @click="goBack">
            Return to Demo Admin
          </v-btn>
        </div>
      </template>
      
      <template v-else>
        <!-- Duck Discovery Section -->
        <DiscoverySection :duck="currentDuck" />
        
        <!-- About Section -->
        <AboutSection v-if="currentCruise" :cruise="currentCruise" class="mb-12" />
        
        <!-- Message Section -->
        <MessageSection
          v-model="message"
          :messages="messages"
          :loading="messagesLoading"
          :show-history="showMessageHistory"
          :duck="currentDuck"
          @send="handleSendMessage"
          @refresh="fetchMessages"
          class="mb-12"
        />
        
        <!-- Social Links Section -->
        <SocialLinksSection v-if="currentCruise" :cruise="currentCruise" @share="handleShare" class="mb-8 sm:mb-12" />

        <!-- Demo Leaderboard CTA (always visible) -->
        <div class="mb-8 sm:mb-12">
          <div class="bg-white/80 backdrop-blur-sm border border-amber-100 rounded-xl p-4 sm:p-5 shadow-sm">
            <div class="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div class="flex items-center gap-3 text-center sm:text-left">
                <div class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <v-icon color="warning">mdi-trophy</v-icon>
                </div>
                <div>
                  <p class="text-sm sm:text-base text-gray-800 font-medium">See who's leading the duck hunt</p>
                  <p class="text-xs text-gray-600">Check the live leaderboard of top finders and hiders</p>
                </div>
              </div>
              <router-link
                :to="{ path: '/demo', hash: '#leaderboard' }"
                class="inline-flex items-center w-full sm:w-auto justify-center px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-medium shadow-sm min-h-[44px]"
              >
                <v-icon size="18" class="mr-2">mdi-podium-gold</v-icon>
                <span class="text-sm">View Demo Leaderboard</span>
              </router-link>
            </div>
          </div>
        </div>

        <!-- Enhanced Duck Collection & Remaining Section -->
        <RemainingRaritySection 
          v-if="mockRemainingDucksByRarity"
          :remaining-ducks-by-rarity="mockRemainingDucksByRarity"
          class="mb-8 sm:mb-12"
        />

        <!-- Demo Duck List Section -->
        <DucksListSection />
      </template>
      
    </div>

    <!-- Feedback Dialog -->
    <v-dialog
      v-model="dialogOpen"
      max-width="500"
      persistent
      scroll-strategy="block"
      retain-focus
      @click:outside="refocusFeedbackDialog"
    >
      <v-card>
        <v-card-title>Message Sent!</v-card-title>
        <v-card-text>
          <p>Your message has been sent to the cruise owner. Thank you for participating in the duck hunt!</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" ref="feedbackCloseBtnRef" @click="dialogOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- UI Overlays (notifications, loading) -->
    <UIOverlays
      v-model:notification="notification"
      :notification-text="notificationText" 
      :notification-color="notificationColor"
      :is-loading="loading"
    />
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, provide, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { UnderwaterBg } from '@/components/ui/underwaterBg';
import { useDemoDuckStore } from '@/stores/demoDuckStore';
import { useDemoCruiseStore } from '@/stores/demoCruiseStore';
import { useDemoMessageStore } from '@/stores/demoMessageStore';
import { ShimmerButton } from '@/components/ui/shimmerButton';
import type { Message } from '@/interfaces/message';

// Import components
import DiscoverySection from '@/components/demoduckview/DiscoverySection.vue';
import MessageSection from '@/components/demoduckview/MessageSection.vue';
import AboutSection from '@/components/demoduckview/AboutSection.vue';
import SocialLinksSection from '@/components/demoduckview/SocialLinksSection.vue';
import DucksListSection from '@/components/demoduckview/DucksListSection.vue';
import RemainingRaritySection from '@/components/demoduckview/RemainingRaritySection.vue';
import UIOverlays from '@/components/demoduckview/UIOverlays.vue';
import ViewToggle from '@/components/demoadmin/ViewToggle.vue';

const route = useRoute();
const router = useRouter();
const duckStore = useDemoDuckStore();
const cruiseStore = useDemoCruiseStore();
const messageStore = useDemoMessageStore();

// View toggle state - set to true initially since we're in Duck View
const isDuckView = ref(true);

// State
const loading = ref(true);
const messagesLoading = ref(false);
const message = ref('');
const notification = ref(false);
const notificationText = ref('');
const notificationColor = ref('success');
const dialogOpen = ref(false);
const messages = ref<Message[]>([]);
const showMessageHistory = ref(true);

// Focus management for feedback dialog
const feedbackCloseBtnRef = ref<HTMLElement | null>(null);
const refocusFeedbackDialog = () => {
  feedbackCloseBtnRef.value?.focus?.();
};
watch(dialogOpen, async (val) => {
  if (val) {
    await nextTick();
    feedbackCloseBtnRef.value?.focus?.();
  }
});

// Computed properties
const currentDuck = computed(() => duckStore.currentDuck);

const currentCruise = computed(() => {
  if (!currentDuck.value?.idCruise) return null;
  return cruiseStore.getCruiseById(currentDuck.value.idCruise);
});

// Mock remaining ducks by rarity for demo - dynamically calculated based on actual demo ducks
const mockRemainingDucksByRarity = computed(() => {
  // Get all ducks from the demo store
  const allDucks = duckStore.ducks;
  
  // Initialize counters
  const totalByRarity = { COMMON: 0, RARE: 0, LEGENDARY: 0 };
  const foundByRarity = { COMMON: 0, RARE: 0, LEGENDARY: 0 };
  
  // Count total and found ducks by rarity
  allDucks.forEach(duck => {
    // Assign a rarity if it doesn't exist (for demo purposes)
    const rarity = duck.rarity || duckStore.assignDemoRarity(duck.id);
    
    totalByRarity[rarity]++;
    if (duck.found === 1) {
      foundByRarity[rarity]++;
    }
  });
  
  // Calculate remaining
  return {
    COMMON: Math.max(0, totalByRarity.COMMON - foundByRarity.COMMON),
    RARE: Math.max(0, totalByRarity.RARE - foundByRarity.RARE),
    LEGENDARY: Math.max(0, totalByRarity.LEGENDARY - foundByRarity.LEGENDARY)
  };
});

// Methods
const goBack = () => {
  router.push('/demo');
};

const handleSendMessage = async () => {
  try {
    if (!message.value.trim() || !currentDuck.value?.uuid) return;
    
    // Add the message using the message store
    messageStore.addDuckMessage(currentDuck.value.uuid, message.value, 'PUBLIC');
    
    // Clear the message input
    message.value = '';
    
    // Show success dialog
    dialogOpen.value = true;
    
    // Refresh messages
    await fetchMessages();
  } catch (error) {
    console.error('Error sending message:', error);
    notificationText.value = 'Failed to send message. Please try again.';
    notificationColor.value = 'error';
    notification.value = true;
  }
};

const fetchMessages = async () => {
  try {
    messagesLoading.value = true;
    if (currentDuck.value?.uuid) {
      messages.value = messageStore.getDuckMessages(currentDuck.value.uuid);
    }
  } catch (error) {
    console.error('Error fetching messages:', error);
  } finally {
    messagesLoading.value = false;
  }
};

const handleShare = () => {
  // In a real app, this would open share dialog with the device's native sharing capabilities
  window.open('https://facebook.com', '_blank');
};

// Initialize
onMounted(async () => {
  try {
    loading.value = true;
    
    // Initialize ducks data
    duckStore.initializeDucks();
    
    // Get duck ID from route params
    const duckId = route.params.id;
    
    if (duckId) {
      // Set the current duck - ensure duckId is a single string
      const singleDuckId = Array.isArray(duckId) ? duckId[0] : duckId;
      const success = duckStore.setCurrentDuck(singleDuckId);
      
      if (success && currentDuck.value) {
        // Mark the duck as found when it's viewed
        if (currentDuck.value.found === 0) {
          // Update duck in duck store
          currentDuck.value.found = 1;
          
          // Update duck in cruise store if needed
          if (currentDuck.value.uuid) {
            const duckInfo = cruiseStore.getDuckByUuid(currentDuck.value.uuid);
            if (duckInfo) {
              duckInfo.duck.found = 1;
            }
          }
        }
        
        // Fetch messages for this duck
        await fetchMessages();
      } else {
        console.error('Duck not found:', duckId);
        // Try to load first duck when ID is invalid
        loadFirstDuck();
      }
    } else {
      // If no duck ID was provided, load the first duck
      loadFirstDuck();
    }
  } catch (error) {
    console.error('Error initializing view:', error);
    notificationText.value = 'Error loading duck information';
    notificationColor.value = 'error';
    notification.value = true;
  } finally {
    loading.value = false;
  }
});

// Load the first available duck
const loadFirstDuck = async () => {
  const ducks = duckStore.ducks;
  if (ducks.length > 0) {
    const firstDuck = ducks[0];
    const success = duckStore.setCurrentDuck(firstDuck.id.toString());
    
    if (success) {
      // Update URL without page reload
      router.replace(`/demo/duck/${firstDuck.id}`);
      await fetchMessages();
      return true;
    }
  }
  return false;
};

// Provide duck and cruise to child components
provide('duck', currentDuck);
provide('cruise', currentCruise);
</script>

<style scoped>
/* Animations */
@keyframes wiggle {
  0%, 100% { transform: rotate(0); }
  25% { transform: rotate(-8deg); }
  75% { transform: rotate(8deg); }
}

.animate-wiggle {
  animation: wiggle 1s ease-in-out infinite;
}

.animate-fade-in {
  animation: fadeIn 0.5s forwards;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Mobile optimizations */
@media (max-width: 640px) {
  .shake-animation {
    animation: enhancedMobileShake 0.5s cubic-bezier(.36,.07,.19,.97) both;
  }
  
  /* Use a simpler animation for better performance on mobile */
  @keyframes enhancedMobileShake {
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-10deg); }
    75% { transform: rotate(10deg); }
  }
}

/* Safe area insets for newer mobile devices */
@supports (padding: max(0px)) {
  main {
    padding-left: env(safe-area-inset-left, 0);
    padding-right: env(safe-area-inset-right, 0);
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
  
  /* Adjust fixed button position for notched phones */
  .fixed.bottom-6.right-6 {
    bottom: max(24px, env(safe-area-inset-bottom, 24px));
    right: max(24px, env(safe-area-inset-right, 24px));
  }
}

/* Improve touch targets for all interactive elements */
:deep(button),
:deep(a),
:deep(.v-btn),
button,
a {
  min-height: 44px;
  min-width: 44px;
  touch-action: manipulation;
}
</style>